import * as v from "valibot";

import {
  type OnboardingAnalyticsEvent,
  validOnboardingEvents,
} from "@textclub/common/bridge";
import { checkMessageKeywordMatch } from "@textclub/common/keywords";
import { createConversationId } from "@textclub/common/random-id";
import type { CustomTextMessage, RichMessage } from "@textclub/common/types";

import { instrumentSdkMethod } from "../instrumentation";
import { GetLoginLinkResponseErrors } from "../login-errors";
import { PlainObject, Record, parseArgs } from "../schema-utils";
import type { ModuleContext } from "./module-context";

const Duration = {
  SECOND: 1000,
} as const;

const LOGIN_REQUEST_TIMEOUT = 10 * Duration.SECOND;
const BROWSER_CONTINUATION_TIMEOUT = 10 * Duration.SECOND;
const VALIDATE_NAME_TIMEOUT = 10 * Duration.SECOND;
const FEATURE_FLAG_TIMEOUT = 10 * Duration.SECOND;

const CustomTextMessageSchema = v.object({
  body: v.string(),
  ctaText: v.string(),
});

const RichMessageSchema = v.object({
  sms: v.string(),
  rcs: PlainObject,
});

const ReserveLoginMessageOpts = v.object({
  message: v.string(),
  replyMessage: v.optional(CustomTextMessageSchema),
  reminderMessage: v.optional(CustomTextMessageSchema),
  replyMessageRaw: v.optional(RichMessageSchema),
  reminderMessageRaw: v.optional(RichMessageSchema),
  keywords: v.optional(v.array(v.string())),
  entryPayload: v.optional(Record),
  targetGameSlug: v.optional(v.string()),
});

const LoginReservationSchema = v.object({
  __type: v.literal("LoginReservation"),
  __token: v.string(),
});

const ReserveBrowserContinuationOpts = v.optional(
  v.object({
    entryPayload: v.optional(Record),
    path: v.optional(v.string()),
  }),
);

const BrowserContinuationReservationSchema = v.object({
  __type: v.literal("BrowserContinuationReservation"),
  __token: v.string(),
});

const OnboardingEventArgs = v.tuple([
  v.picklist(validOnboardingEvents, "unknown onboarding event"),
  v.optional(Record),
]);

const ValidateNameArgs = v.tuple([v.string()]);

const FeatureFlagArgs = v.tuple([v.string()]);

const RedirectToFlagshipGameOpts = v.optional(
  v.object({
    entryPayload: v.optional(Record),
    path: v.optional(v.string()),
  }),
);

const RedirectToGameOpts = v.object({
  gameSlug: v.string(),
  entryPayload: v.optional(Record),
  skipGameExitConfirm: v.optional(v.boolean()),
});

/**
 * InternalModule hosts non-public SDK functionality.
 */
export class InternalModule {
  private tokenToLinkMap: Map<string, string> = new Map();

  constructor(private ctx: ModuleContext) {
    this.ctx.registerMessageHandlers({
      LoginLeaseAcquired: (message) => this.ctx.resolveWaiting(message),
      BrowserContinuationReserved: (message) =>
        this.ctx.resolveWaiting(message),
      ValidateNameResponse: (message) => this.ctx.resolveWaiting(message),
      GetFeatureFlagResponse: (message) => this.ctx.resolveWaiting(message),
    });
  }

  @instrumentSdkMethod({ name: "internal.reserveLoginMessageAsync" })
  async reserveLoginMessageAsync(options: {
    message: string;
    replyMessage?: CustomTextMessage;
    reminderMessage?: CustomTextMessage;
    replyMessageRaw?: RichMessage;
    reminderMessageRaw?: RichMessage;
    keywords?: string[];
    entryPayload?: Record<string, unknown>;
    targetGameSlug?: string;
  }): Promise<
    | { reservation: { __type: "LoginReservation"; __token: string } }
    | {
        error:
          | typeof GetLoginLinkResponseErrors.COULD_NOT_ACQUIRE_LEASE
          | typeof GetLoginLinkResponseErrors.INVALID_TARGET_GAME;
      }
  > {
    parseArgs(ReserveLoginMessageOpts, options);

    if (
      options.keywords &&
      !checkMessageKeywordMatch(options.message, options.keywords)
    ) {
      throw new Error(
        "INVALID_ARGUMENTS: Keywords must be present in message in same order",
      );
    }

    if (options.replyMessage && options.replyMessageRaw) {
      throw new Error(
        "INVALID_ARGUMENTS: Cannot set both replyMessage and replyMessageRaw",
      );
    }

    if (options.reminderMessage && options.reminderMessageRaw) {
      throw new Error(
        "INVALID_ARGUMENTS: Cannot set both reminderMessage and reminderMessageRaw",
      );
    }

    if (this.ctx.isLoggedIn()) {
      throw new Error("Player already logged in");
    }

    const { message, keywords, entryPayload } = options;
    const conversationId: string = createConversationId();

    this.ctx.sendMessage({
      type: "AcquireLoginLease",
      message,
      replyMessage: options.replyMessage ?? null,
      reminderMessage: options.reminderMessage ?? null,
      replyMessageRaw: options.replyMessageRaw ?? null,
      reminderMessageRaw: options.reminderMessageRaw ?? null,
      keywords: keywords ?? null,
      entryPayload: JSON.stringify(entryPayload),
      targetGameSlug: options.targetGameSlug ?? null,
      conversationId: conversationId,
    });

    const response = await this.ctx.waitForNextMessage(
      "LoginLeaseAcquired",
      conversationId,
      LOGIN_REQUEST_TIMEOUT,
    );

    if (!response.success) {
      switch (response.error) {
        case "invalid_target_game":
          return {
            error: GetLoginLinkResponseErrors.INVALID_TARGET_GAME,
          };
        case "internal_error":
          return {
            error: GetLoginLinkResponseErrors.COULD_NOT_ACQUIRE_LEASE,
          };
        default:
          return {
            error: GetLoginLinkResponseErrors.COULD_NOT_ACQUIRE_LEASE,
          };
      }
    }

    const token = `${Date.now().toString(36)}${Math.random().toString(36).substring(2, 8)}`;
    this.tokenToLinkMap.set(token, response.link);
    const reservation = {
      __type: "LoginReservation" as const,
      __token: token,
    };
    return { reservation };
  }

  @instrumentSdkMethod({ name: "internal.sendReservedLoginMessage" })
  sendReservedLoginMessage(reservation: {
    __type: "LoginReservation";
    __token: string;
  }): void {
    parseArgs(LoginReservationSchema, reservation);

    const link = this.tokenToLinkMap.get(reservation.__token);
    if (!link) {
      throw new Error("Invalid or expired reservation");
    }

    this.ctx.sendMessage({
      type: "OnboardingCaptureEvent",
      event: "sms_button_tap",
    });

    this.ctx.sendMessage({
      type: "SendReservedLoginMessage",
      link,
    });
  }

  @instrumentSdkMethod({ name: "internal.reserveBrowserContinuationLink" })
  async reserveBrowserContinuationLink(options?: {
    entryPayload?: Record<string, unknown>;
    path?: string;
  }): Promise<
    | {
        reservation: {
          __type: "BrowserContinuationReservation";
          __token: string;
        };
      }
    | { error: "no_target_game" | "invalid_path" | "could_not_reserve" }
  > {
    parseArgs(ReserveBrowserContinuationOpts, options);

    const conversationId = createConversationId();
    this.ctx.sendMessage({
      type: "ReserveBrowserContinuation",
      entryPayload:
        options?.entryPayload === undefined
          ? undefined
          : JSON.stringify(options.entryPayload),
      path: options?.path,
      conversationId,
    });

    const response = await this.ctx.waitForNextMessage(
      "BrowserContinuationReserved",
      conversationId,
      BROWSER_CONTINUATION_TIMEOUT,
    );

    if (!response.success) {
      return {
        error:
          response.error === "not_entitled"
            ? "could_not_reserve"
            : response.error,
      };
    }

    return {
      reservation: {
        __type: "BrowserContinuationReservation",
        __token: response.token,
      },
    };
  }

  @instrumentSdkMethod({ name: "internal.openReservedBrowserContinuationLink" })
  openReservedBrowserContinuationLink(reservation: {
    __type: "BrowserContinuationReservation";
    __token: string;
  }): void {
    parseArgs(BrowserContinuationReservationSchema, reservation);

    this.ctx.sendMessage({
      type: "OpenReservedBrowserContinuation",
      token: reservation.__token,
    });
  }

  @instrumentSdkMethod({ name: "internal.captureOnboardingEvent" })
  captureOnboardingEvent(
    event: OnboardingAnalyticsEvent,
    properties?: Record<string, unknown>,
  ) {
    parseArgs(OnboardingEventArgs, [event, properties]);

    this.ctx.assertInitialized();
    this.ctx.sendMessage({
      type: "OnboardingCaptureEvent",
      event: event,
      properties,
    });
  }

  @instrumentSdkMethod({ name: "internal.redirectToExplorePage" })
  redirectToExplorePage(): void {
    this.ctx.sendMessage({ type: "RedirectToExplorePage" });
  }

  @instrumentSdkMethod({ name: "internal.redirectToFlagshipGame" })
  redirectToFlagshipGame(options?: {
    entryPayload?: Record<string, unknown>;
  }): void {
    this._coreRedirectToFlagshipGame(options);
  }

  @instrumentSdkMethod({ name: "internal.redirectToGame" })
  redirectToGame(options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
    skipGameExitConfirm?: boolean;
  }): void {
    this._coreRedirectToGame(options);
  }

  @instrumentSdkMethod({ name: "internal.openPrivacyPolicy" })
  openPrivacyPolicy(): void {
    this._coreOpenPrivacyPolicy();
  }

  @instrumentSdkMethod({ name: "internal.openTermsOfService" })
  openTermsOfService(): void {
    this._coreOpenTermsOfService();
  }

  @instrumentSdkMethod({ name: "internal.openCopyright" })
  openCopyright(): void {
    this._coreOpenCopyright();
  }

  @instrumentSdkMethod({ name: "internal.validateName" })
  async validateName(nameValue: string): Promise<
    | {
        status: "invalid";
        validationError: "too_short" | "too_long" | "inappropriate";
      }
    | { status: "valid" }
  > {
    parseArgs(ValidateNameArgs, [nameValue]);

    this.ctx.assertInitialized();

    const conversationId = createConversationId();
    this.ctx.sendMessage({
      type: "ValidateName",
      conversationId,
      name: nameValue,
    });

    const response = await this.ctx.waitForNextMessage(
      "ValidateNameResponse",
      conversationId,
      VALIDATE_NAME_TIMEOUT,
    );

    if (!response.success) {
      throw new Error(response.error);
    }

    if (response.status === "invalid") {
      return {
        status: "invalid",
        validationError: response.validationError,
      };
    }

    return {
      status: "valid",
    };
  }

  @instrumentSdkMethod({ name: "internal.getFeatureFlag" })
  async getFeatureFlag(key: string): Promise<string | undefined> {
    parseArgs(FeatureFlagArgs, [key]);

    this.ctx.assertInitialized();

    const conversationId = createConversationId();
    this.ctx.sendMessage({
      type: "GetFeatureFlag",
      conversationId,
      key,
    });

    const response = await this.ctx.waitForNextMessage(
      "GetFeatureFlagResponse",
      conversationId,
      FEATURE_FLAG_TIMEOUT,
    );

    if (!response.success) {
      throw new Error(response.error);
    }

    return response.value;
  }

  // === Core Non-Instrumented Methods (for deprecated method delegation) ===

  _coreRedirectToFlagshipGame(options?: {
    entryPayload?: Record<string, unknown>;
  }): void {
    parseArgs(RedirectToFlagshipGameOpts, options);

    let entryPayloadString: string | undefined;

    if (options?.entryPayload !== undefined) {
      entryPayloadString = JSON.stringify(options.entryPayload);
    }

    this.ctx.sendMessage({
      type: "RedirectToGame",
      redirectToFlagship: true,
      entryPayload: entryPayloadString,

      // Redirecting to flagship from an onboarding always show the exit dialog,
      // so we have more chances to retain the user.
      skipGameExitConfirm: false,
    });
  }

  _coreRedirectToGame(options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
    skipGameExitConfirm?: boolean;
  }): void {
    parseArgs(RedirectToGameOpts, options);

    this.ctx.sendMessage({
      type: "RedirectToGame",
      gameSlug: options.gameSlug,
      entryPayload: JSON.stringify(options.entryPayload),
      skipGameExitConfirm: options.skipGameExitConfirm ?? false,
    });
  }

  _coreOpenPrivacyPolicy(): void {
    this.ctx.sendMessage({
      type: "OpenLegalPage",
      page: "privacy",
    });
  }

  _coreOpenTermsOfService(): void {
    this.ctx.sendMessage({
      type: "OpenLegalPage",
      page: "terms",
    });
  }

  _coreOpenCopyright(): void {
    this.ctx.sendMessage({
      type: "OpenLegalPage",
      page: "copyright",
    });
  }

  /** Exposed for decorator use - delegates to context */
  instrumentMethod<T>(opts: { method: string; args: unknown; fn: () => T }): T {
    return this.ctx.instrumentMethod(opts);
  }
}
